/**
 * Theme Manager — KollegePlus Messenger
 *
 * Handles dark/light mode with:
 * - System preference detection (prefers-color-scheme)
 * - Manual toggle with persistence
 * - Smooth transitions between themes
 */

export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "kollegeplus-theme";

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredTheme(): Theme {
  return (localStorage.getItem(STORAGE_KEY) as Theme) ?? "system";
}

function applyTheme(theme: Theme): void {
  const resolved = theme === "system" ? getSystemTheme() : theme;
  const root = document.documentElement;

  root.setAttribute("data-theme", resolved);

  // Update meta theme-color for mobile browsers
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", resolved === "dark" ? "#0D0D0F" : "#FFFFFF");
  }
}

export function initTheme(): void {
  const theme = getStoredTheme();
  applyTheme(theme);

  // Listen for system theme changes
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (getStoredTheme() === "system") {
      applyTheme("system");
    }
  });
}

export function setTheme(theme: Theme): void {
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

export function getCurrentTheme(): Theme {
  return getStoredTheme();
}

export function getResolvedTheme(): "light" | "dark" {
  const theme = getStoredTheme();
  return theme === "system" ? getSystemTheme() : theme;
}

export function toggleTheme(): void {
  const current = getResolvedTheme();
  setTheme(current === "dark" ? "light" : "dark");
}
